import React, { useMemo, useState } from 'react';
import {
  IconButton, MessageBar, MessageBarType, Stack, Text,
} from '@fluentui/react';
import {
  BillitDetailSection,
  BillitKvGrid,
  billitColors,
  billitRadius,
} from './billitDetailUi';
import { formatUblXmlForDisplay } from './formatUblXmlForDisplay';
import {
  formatUblAmount,
  parseUblInvoice,
  UblInvoiceLine,
} from './parseUblInvoice';

type Props = {
  title?: string;
  xmlContent: string;
  height?: number | string;
  fillContainer?: boolean;
  fileName?: string;
};

const partyItems = (
  party: ReturnType<typeof parseUblInvoice>['supplier'],
  prefix: string,
) => [
  { label: `${prefix}`, value: party?.name, highlight: true },
  { label: 'BTW nr.', value: party?.vatNumber },
  { label: 'Adres', value: party?.address },
  { label: 'E-mail', value: party?.email },
  { label: 'Telefoon', value: party?.phone },
];

const lineColumns: Array<{
  key: keyof UblInvoiceLine;
  label: string;
  align?: 'left' | 'right';
}> = [
  { key: 'description', label: 'Omschrijving' },
  { key: 'quantity', label: 'Aantal', align: 'right' },
  { key: 'unitPrice', label: 'Eenheidsprijs', align: 'right' },
  { key: 'vatPercent', label: 'BTW %', align: 'right' },
  { key: 'lineTotal', label: 'Totaal', align: 'right' },
];

const formatLineValue = (
  line: UblInvoiceLine,
  key: keyof UblInvoiceLine,
  currency: string | null,
) => {
  if (key === 'quantity' && line.quantity) {
    return line.unit ? `${line.quantity} ${line.unit}` : line.quantity;
  }
  if (key === 'unitPrice' || key === 'lineTotal') {
    return formatUblAmount(line[key], currency);
  }
  if (key === 'vatPercent' && line.vatPercent) {
    return `${line.vatPercent}%`;
  }
  return line[key] || '—';
};

const BillitUblMarkupView = ({
  title = 'UBL factuur',
  xmlContent,
  height = 420,
  fillContainer = false,
  fileName = 'factuur.xml',
}: Props) => {
  const [showRawXml, setShowRawXml] = useState(false);
  const parsed = useMemo(() => parseUblInvoice(xmlContent), [xmlContent]);
  const formattedXml = useMemo(
    () => formatUblXmlForDisplay(xmlContent),
    [xmlContent],
  );

  const viewerHeight = fillContainer ? '100%' : height;
  const viewerWrapStyle: React.CSSProperties | undefined = fillContainer
    ? {
      position: 'relative',
      flex: 1,
      minHeight: 400,
      width: '100%',
    }
    : undefined;
  const viewerInnerStyle: React.CSSProperties = fillContainer
    ? {
      border: `1px solid ${billitColors.borderLight}`,
      borderRadius: billitRadius.card,
      overflow: 'auto',
      position: 'absolute',
      inset: 0,
      background: '#fff',
    }
    : {
      border: `1px solid ${billitColors.borderLight}`,
      borderRadius: billitRadius.card,
      overflow: 'auto',
      height: viewerHeight,
      background: '#fff',
    };

  const downloadXml = () => {
    const blob = new Blob([xmlContent], { type: 'text/xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const documentTitle =
    parsed.documentType === 'CreditNote' ? 'Creditnota' : 'Factuur';

  return (
    <Stack
      tokens={{ childrenGap: 8 }}
      styles={
        fillContainer
          ? {
            root: {
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            },
          }
          : undefined
      }
    >
      <Stack horizontal horizontalAlign='space-between' verticalAlign='center'>
        <Text style={{ fontWeight: 600, fontSize: 13 }}>{title}</Text>
        <Stack horizontal tokens={{ childrenGap: 4 }}>
          <IconButton
            iconProps={{ iconName: showRawXml ? 'RedEye' : 'Code' }}
            title={showRawXml ? 'Toon UBL markup' : 'Toon ruwe XML'}
            onClick={() => setShowRawXml(prev => !prev)}
          />
          <IconButton
            iconProps={{ iconName: 'Download' }}
            title='Download XML'
            onClick={downloadXml}
          />
        </Stack>
      </Stack>

      <MessageBar messageBarType={MessageBarType.info} styles={{ root: { marginBottom: 4 } }}>
        Geen PDF in UBL — gestructureerde {documentTitle.toLowerCase()}-weergave (UBL).
      </MessageBar>

      <div style={viewerWrapStyle}>
        <div style={viewerInnerStyle}>
          {parsed.parseError ? (
            <Stack tokens={{ childrenGap: 12 }} styles={{ root: { padding: 12 } }}>
              <MessageBar messageBarType={MessageBarType.warning}>{parsed.parseError}</MessageBar>
              <pre
                style={{
                  margin: 0,
                  fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                  fontSize: 11,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {formattedXml}
              </pre>
            </Stack>
          ) : showRawXml ? (
            <pre
              style={{
                margin: 0,
                padding: 12,
                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                fontSize: 11,
                lineHeight: 1.45,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {formattedXml}
            </pre>
          ) : (
            <Stack tokens={{ childrenGap: 12 }} styles={{ root: { padding: 12 } }}>
              <div
                style={{
                  padding: '12px 14px',
                  borderRadius: billitRadius.card,
                  background: billitColors.surface,
                  border: `1px solid ${billitColors.borderLight}`,
                }}
              >
                <Text style={{ fontSize: 12, color: billitColors.textMuted }}>
                  {documentTitle}
                </Text>
                <Text
                  style={{
                    display: 'block',
                    fontSize: 20,
                    fontWeight: 700,
                    color: billitColors.purple,
                    marginTop: 4,
                  }}
                >
                  {parsed.invoiceNumber || '—'}
                </Text>
                <BillitKvGrid
                  items={[
                    { label: 'Factuurdatum', value: parsed.issueDate },
                    { label: 'Vervaldatum', value: parsed.dueDate },
                    { label: 'Valuta', value: parsed.currency },
                    { label: 'Referentie', value: parsed.buyerReference },
                    { label: 'Bestelbon', value: parsed.orderReference },
                    { label: 'Betalingsref.', value: parsed.paymentReference },
                  ]}
                />
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: 12,
                }}
              >
                <BillitDetailSection title='Leverancier' defaultCollapsed={false} level={2}>
                  <BillitKvGrid items={partyItems(parsed.supplier, 'Naam')} />
                </BillitDetailSection>
                <BillitDetailSection title='Klant' defaultCollapsed={false} level={2}>
                  <BillitKvGrid items={partyItems(parsed.customer, 'Naam')} />
                </BillitDetailSection>
              </div>

              {parsed.lines.length > 0 && (
                <BillitDetailSection title='Factuurregels' defaultCollapsed={false} level={2}>
                  <div style={{ overflowX: 'auto' }}>
                    <table
                      style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: 12,
                      }}
                    >
                      <thead>
                        <tr style={{ background: billitColors.surface }}>
                          {lineColumns.map(column => (
                            <th
                              key={column.key}
                              style={{
                                textAlign: column.align || 'left',
                                padding: '8px 10px',
                                borderBottom: `1px solid ${billitColors.borderLight}`,
                                fontWeight: 600,
                                color: billitColors.textMuted,
                              }}
                            >
                              {column.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {parsed.lines.map((line, index) => (
                          <tr key={`${line.lineId || line.description || 'line'}-${index}`}>
                            {lineColumns.map(column => (
                              <td
                                key={column.key}
                                style={{
                                  textAlign: column.align || 'left',
                                  padding: '8px 10px',
                                  borderBottom: `1px solid ${billitColors.borderLight}`,
                                  verticalAlign: 'top',
                                  color: billitColors.text,
                                }}
                              >
                                {formatLineValue(line, column.key, parsed.currency)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </BillitDetailSection>
              )}

              <div
                style={{
                  marginLeft: 'auto',
                  minWidth: 240,
                  maxWidth: 320,
                  padding: '12px 14px',
                  borderRadius: billitRadius.card,
                  background: billitColors.neutralBg,
                  border: `1px solid ${billitColors.borderLight}`,
                }}
              >
                <Stack tokens={{ childrenGap: 6 }}>
                  <Stack horizontal horizontalAlign='space-between'>
                    <Text style={{ fontSize: 12, color: billitColors.textMuted }}>Totaal excl.</Text>
                    <Text style={{ fontSize: 12 }}>
                      {formatUblAmount(parsed.taxExclusiveAmount, parsed.currency)}
                    </Text>
                  </Stack>
                  <Stack horizontal horizontalAlign='space-between'>
                    <Text style={{ fontSize: 12, color: billitColors.textMuted }}>BTW</Text>
                    <Text style={{ fontSize: 12 }}>
                      {formatUblAmount(parsed.taxAmount, parsed.currency)}
                    </Text>
                  </Stack>
                  <Stack horizontal horizontalAlign='space-between'>
                    <Text style={{ fontSize: 12, color: billitColors.textMuted }}>Totaal incl.</Text>
                    <Text style={{ fontSize: 12 }}>
                      {formatUblAmount(parsed.taxInclusiveAmount, parsed.currency)}
                    </Text>
                  </Stack>
                  <div style={{ borderTop: `1px solid ${billitColors.borderLight}`, paddingTop: 8 }}>
                    <Stack horizontal horizontalAlign='space-between'>
                      <Text style={{ fontSize: 13, fontWeight: 700, color: billitColors.purple }}>
                        Te betalen
                      </Text>
                      <Text style={{ fontSize: 13, fontWeight: 700, color: billitColors.purple }}>
                        {formatUblAmount(parsed.payableAmount, parsed.currency)}
                      </Text>
                    </Stack>
                  </div>
                </Stack>
              </div>

              {parsed.note && (
                <BillitDetailSection title='Opmerking' defaultCollapsed={false} level={2}>
                  <Text style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{parsed.note}</Text>
                </BillitDetailSection>
              )}
            </Stack>
          )}
        </div>
      </div>
    </Stack>
  );
};

export default BillitUblMarkupView;
